import React from 'react'
import { Box } from '@mui/material'
import { Add } from '@mui/icons-material'
import {
  CrudAction,
  Entity,
  usePageHeader,
  usePagination,
  usePermissions,
} from 'utils/hooks'
import { Button, Card } from 'components/ui'
import { Table } from '../../../../components'
import * as sxStyles from './Analytics.styled'
import { analyticsColumns } from './columns'
import { useNavigate } from 'react-router-dom'
import useNewAnalysisModal from '../../dialogs/useNewAnalysisModal'
import { useQuery } from 'react-query'
import { useAnalyticsServices } from '../services/services'
import { EModule } from '../../../../types'

export type AnalyticsListMode = 'production-line' | 'post-aging' | 'products'

type AnalyticsPagesProps = {
  mode?: AnalyticsListMode
}

const AnalyticsPageTitle: Record<AnalyticsListMode, string> = {
  'production-line': 'Flours - Production Line',
  'post-aging': 'Flours - Post Aging',
  products: 'Products',
}

export const Analytics = ({ mode }: AnalyticsPagesProps) => {
  const { checkPermission } = usePermissions()
  const navigate = useNavigate()
  const isPostAging = mode === 'post-aging'
  const isBakerModule = mode === 'products'
  const { getProductsWithDeliveries } = useAnalyticsServices(isBakerModule)
  const [renderNewAnalysisModal, toggleNewAnalysisModal] =
    useNewAnalysisModal(mode)

  const isUploading = !!localStorage.getItem(
    isBakerModule ? `baker_nir_job_id` : `flour_nir_job_id`
  )

  usePageHeader(
    {
      headerTitle: AnalyticsPageTitle[mode],
      actionButton: checkPermission(
        isBakerModule ? Entity.BAKER_ANALYTICS : Entity.FLOURS,
        CrudAction.CREATE
      ) && (
        <Button
          startIcon={<Add />}
          onClick={() => toggleNewAnalysisModal()}
          disabled={isUploading}
        >
          New Analysis
        </Button>
      ),
    },
    true,
    [isUploading]
  )

  const { currentPage, rowsPerPage } = usePagination({})

  const { data } = useQuery({
    queryKey: [
      'products-with-deliveries',
      isPostAging,
      currentPage,
      rowsPerPage,
    ],
    queryFn: () =>
      getProductsWithDeliveries({
        filters: { post_aging: isBakerModule ? undefined : isPostAging },
        pagination: {
          offset: currentPage * rowsPerPage,
          limit: rowsPerPage,
        },
        order_by: 'string',
      }),
    keepPreviousData: true,
  })

  const analyticsData =
    data?.results.map((item) => ({
      id: item.id,
      name: item.name,
      last_entry: item.last_entry,
      total_entries: item.total_entries,
      consistent: item.consistent_entries,
      inconsistent: item.inconsistent_entries,
      creation_date: item.created,
      created_by: item.created_by,
    })) ?? []

  const handleViewAnalyticsDetails = (productId?: number) => {
    if (mode === 'products') {
      navigate(`/${mode}/${productId}`)
    } else {
      navigate(`/flours/${mode}/${productId}`)
    }
  }

  return (
    <Card sx={sxStyles.container}>
      <Box sx={sxStyles.listWrapper}>
        <Table
          columns={analyticsColumns()}
          data={analyticsData}
          onRowClick={(row) => handleViewAnalyticsDetails(row.id)}
          showHeader={true}
          showPagination
          total={data?.pagination?.total}
        />
      </Box>
      {renderNewAnalysisModal}
    </Card>
  )
}
